import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getHostTenantSlug, getTenantSlug, isMainDomain } from '../utils/tenantResolver';
import { apiService } from '../services/apiService';
import { isSuperAdminTenantSlug } from '../utils/superAdmin';

interface Organization {
  id: number;
  name: string;
  slug: string;
  planType: string;
  role?: string;
}

interface TenantContextType {
  organization: Organization | null;
  tenantSlug: string | null;
  isMainDomain: boolean;
  isTenantDomain: boolean;
  /** When on tenant subdomain: null = loading, true = valid tenant, false = invalid/unregistered tenant */
  isTenantValid: boolean | null;
  setOrganization: (org: Organization | null) => void;
  switchOrganization: (orgSlug: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [isMainDomainState, setIsMainDomainState] = useState(false);
  const [isTenantValid, setIsTenantValid] = useState<boolean | null>(null);

  useEffect(() => {
    // Detect tenant from hostname
    const slug = getTenantSlug();
    setTenantSlug(slug);
    setIsMainDomainState(isMainDomain());

    // Tenant org is resolved from URL + authenticated API only — never from cached localStorage.
    const hostSlug = getHostTenantSlug();
    if (hostSlug) {
      setTenantSlug(hostSlug);
    }
    try {
      localStorage.removeItem('currentOrganization');
    } catch {
      /* ignore */
    }
  }, []);

  // Validate tenant with backend when on a tenant subdomain (before showing app or login)
  // Persist only positive results in sessionStorage so a transient failure never blocks login handoff
  useEffect(() => {
    const storageKey = `tenant_valid_${window.location.hostname}`;
    let exchangeToken: string | null = null;
    let authHandoffActive = false;
    try {
      const params = new URLSearchParams(window.location.search);
      exchangeToken = params.get('exchangeToken');
      if (params.get('sessionReplaced') === '1') {
        sessionStorage.removeItem(storageKey);
      }
      const untilRaw = sessionStorage.getItem('auth_handoff_until');
      const until = untilRaw ? Number(untilRaw) : 0;
      authHandoffActive = Number.isFinite(until) && until > Date.now();
    } catch {
      /* ignore */
    }
    if (isMainDomain()) {
      setIsTenantValid(true);
      return;
    }
    const slug = getTenantSlug();
    if (!slug) {
      setIsTenantValid(false);
      return;
    }
    // Reserved host for platform super-admins — no organization row; still a valid app entry.
    if (isSuperAdminTenantSlug(slug)) {
      setIsTenantValid(true);
      sessionStorage.setItem(storageKey, 'true');
      return;
    }
    // Cross-subdomain login handoff must mount /login — do not block on stale "tenant invalid" cache.
    if (exchangeToken || authHandoffActive) {
      sessionStorage.removeItem(storageKey);
      setIsTenantValid(true);
      return;
    }
    const cached = sessionStorage.getItem(storageKey);
    if (cached === 'true') {
      setIsTenantValid(true);
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const applyResult = (valid: boolean) => {
      if (cancelled) return;
      setIsTenantValid(valid);
      if (valid) {
        sessionStorage.setItem(storageKey, 'true');
      } else {
        sessionStorage.removeItem(storageKey);
      }
    };

    apiService
      .validateTenant()
      .then((res: { valid: boolean }) => applyResult(res.valid))
      .catch(() => {
        // Transient failure: retry once (do not cache false — that mimics "org not registered")
        retryTimer = setTimeout(() => {
          apiService
            .validateTenant()
            .then((res: { valid: boolean }) => applyResult(res.valid))
            .catch(() => {
              if (cancelled) return;
              setIsTenantValid(false);
            });
        }, 400);
      });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  const handleSetOrganization = (org: Organization | null) => {
    setOrganization(org);
    if (org) {
      localStorage.setItem('currentOrganization', JSON.stringify(org));
      setTenantSlug(org.slug);
    } else {
      localStorage.removeItem('currentOrganization');
      setTenantSlug(null);
    }
  };

  const switchOrganization = (orgSlug: string) => {
    // This will redirect to the tenant subdomain
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      window.location.href = `${protocol}//${orgSlug}.localhost${port}/`;
    } else {
      const domainParts = hostname.split('.');
      if (domainParts.length >= 2) {
        const domain = domainParts.slice(-2).join('.');
        window.location.href = `${protocol}//${orgSlug}.${domain}${port}/`;
      }
    }
  };

  const value: TenantContextType = {
    organization,
    tenantSlug,
    isMainDomain: isMainDomainState,
    isTenantDomain: !isMainDomainState && tenantSlug !== null,
    isTenantValid,
    setOrganization: handleSetOrganization,
    switchOrganization,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

